import { ArrowRight, Sparkles } from "lucide-react";
import { motion, type Variants } from "framer-motion";
import heroImage from "@/assets/hero-image.jpg";
import ParticleField from "@/components/hero/ParticleField";
import MagneticButton from "@/components/hero/MagneticButton";

const container: Variants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.15, delayChildren: 0.1 },
  },
};

const item: Variants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const Hero = () => {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth"
      });
    }
  };

  return (
    <section id="inicio" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      <div className="absolute inset-0 bg-hero-gradient opacity-10 z-0" />
      {/* Decorative gold blobs */}
      <div aria-hidden="true" className="absolute -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-primary/20 blur-3xl animate-blob z-0" />
      <div aria-hidden="true" className="absolute -bottom-32 -right-32 w-[32rem] h-[32rem] rounded-full bg-accent/20 blur-3xl animate-blob z-0" style={{ animationDelay: "3s" }} />
      {/* Hero background image with optimized loading */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <img 
          src={heroImage}
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover opacity-[0.04]"
          fetchPriority="high"
          decoding="async"
        />
      </div>

      {/* Interactive particle mesh */}
      <ParticleField />

      <div className="container mx-auto px-4 py-20 relative z-10">
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="max-w-4xl mx-auto text-center flex flex-col items-center space-y-8"
        >
          <motion.span
            variants={item}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium border border-primary/20"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Marketing especializado em Pet & Nutrição
          </motion.span>

          <motion.h1
            variants={item}
            className="font-heading font-bold text-balance text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.1] text-foreground"
          >
            Onde Estratégia de Marketing e{" "}
            <span className="bg-hero-gradient bg-clip-text text-transparent">
              Inteligência Artificial
            </span>{" "}
            se encontram para escalar o seu negócio.
          </motion.h1>
          
          <motion.p
            variants={item}
            className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto text-balance"
          >
            Unimos a inteligência do marketing tradicional a agentes autônomos e 
            automações de ponta para transformar marcas de saúde, nutrição e 
            bem-estar pet em autoridades digitais.
          </motion.p>

          <motion.div
            variants={item}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <MagneticButton variant="primary" onClick={() => scrollToSection("servicos")}>
              Explorar Soluções
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
            </MagneticButton>
            <MagneticButton variant="outline" onClick={() => scrollToSection("contato")}>
              Falar com Especialistas
            </MagneticButton>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
